import { create } from 'zustand';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/services/db/supabase';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

function decodeJWTPayload(token: string): Record<string, unknown> {
  try {
    const base64Payload = token.split('.')[1];
    const payload = atob(
      base64Payload.replace(/-/g, '+').replace(/_/g, '/')
    );
    return JSON.parse(payload);
  } catch {
    return {};
  }
}

export type UserRole =
  | 'platform_admin'
  | 'tenant_admin'
  | 'safety_manager'
  | 'supervisor'
  | 'ssr'
  | 'viewer'
  | 'executive';

interface AuthState {
  user: User | null;
  session: Session | null;
  userRole: UserRole | null;
  tenantId: string | null;
  projectId: string | null;
  assignedScope: unknown;
  isLoading: boolean;
  isInitialized: boolean;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

function extractClaims(session: Session) {
  const token = session.access_token;
  const claims = token ? decodeJWTPayload(token) : {};
  return {
    userRole: (claims.user_role as UserRole) ?? null,
    tenantId: (claims.tenant_id as string) ?? null,
    projectId: (claims.project_id as string) ?? null,
    assignedScope: claims.assigned_scope ?? null,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  userRole: null,
  tenantId: null,
  projectId: null,
  assignedScope: null,
  isLoading: true,
  isInitialized: false,

  initialize: async () => {
    try {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        const claims = extractClaims(data.session);
        set({
          user: data.session.user,
          session: data.session,
          ...claims,
        });
        setSentryUser({
          userId: data.session.user.id,
          role: claims.userRole,
          tenantId: claims.tenantId,
        });
      }
    } finally {
      set({ isLoading: false, isInitialized: true });
    }

    supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        set({
          user: null,
          session: null,
          userRole: null,
          tenantId: null,
          projectId: null,
          assignedScope: null,
          isInitialized: true,
        });
        clearSentryUser();
      } else if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        const claims = extractClaims(session);
        set({
          user: session.user,
          session,
          ...claims,
          isInitialized: true,
        });
        setSentryUser({
          userId: session.user.id,
          role: claims.userRole,
          tenantId: claims.tenantId,
        });
      }
    });
  },

  signIn: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      if (!data.session) throw new Error('No session returned');

      const claims = extractClaims(data.session);
      set({
        user: data.session.user,
        session: data.session,
        ...claims,
        isLoading: false,
      });
      setSentryUser({
        userId: data.session.user.id,
        role: claims.userRole,
        tenantId: claims.tenantId,
      });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({
      user: null,
      session: null,
      userRole: null,
      tenantId: null,
      projectId: null,
      assignedScope: null,
      isLoading: false,
    });
    clearSentryUser();
  },
}));
